import { Channel } from './channel.mjs';

/**
 * The transport-level pointer of beyond-cdn-events/1: what a notifier broadcasts, under the event name
 * `beyond-cdn-pointer/1`, instead of events that will not travel. It says that the channel reached `sequence`
 * and nothing else, so the consumer replays from its cursor.
 *
 * A pointer is not an event. It has no id, no type and no sequence of its own, it is never persisted and a
 * replay never returns it.
 */
export class Pointer {
	/**
	 * Broadcast event name that carries a pointer, next to the one that carries a microbatch
	 */
	static EVENT = 'beyond-cdn-pointer/1';

	/**
	 * `oversize`: the event cannot fit one message. `stale`: its notification waited longer than the notify age.
	 */
	static REASONS = Object.freeze(['oversize', 'stale']);

	#channel;
	get channel() {
		return this.#channel;
	}

	#sequence;
	/**
	 * The channel reached at least this sequence
	 */
	get sequence() {
		return this.#sequence;
	}

	#reason;
	get reason() {
		return this.#reason;
	}

	/**
	 * @param {{channel: string, sequence: number, reason: 'oversize' | 'stale'}} specs
	 * @throws {RangeError} When a member is not one the contract allows
	 */
	constructor({ channel, sequence, reason }) {
		if (!Channel.parse(channel)) throw new RangeError(`Invalid channel "${channel}"`);
		if (!Number.isSafeInteger(sequence) || sequence < 1) throw new RangeError(`Invalid sequence "${sequence}"`);
		if (!Pointer.REASONS.includes(reason)) throw new RangeError(`Invalid reason "${reason}"`);
		this.#channel = channel;
		this.#sequence = sequence;
		this.#reason = reason;
	}

	/**
	 * Tells a pointer from a microbatch or a replay page by its members, for a consumer that receives both
	 * through one handler.
	 *
	 * @param {unknown} message The payload of a broadcast message
	 * @returns {Pointer | undefined} undefined for anything that is not a well-formed pointer
	 */
	static read(message) {
		if (!message || typeof message !== 'object' || 'events' in message || !('reason' in message)) return;
		try {
			return new Pointer(message);
		} catch {}
	}
}
